/**
 * Offline cache adapter for organization settings.
 * Supabase is the source of truth when online; localStorage is the offline cache.
 */

import {
  DEFAULT_ORG_SETTINGS,
  getEffectiveOrgSetting,
  mergeAppSettings,
} from "./organizationSettings";
import {
  getActiveCacheOrganizationId,
  readOrgScopedJson,
  writeOrgScopedJson,
} from "./orgCacheStorage";

const STORAGE_KEY = "CP_ORG_SETTINGS_V1";
const ORG_ID_KEY = "CP_ORGANIZATION_ID";
const MIGRATION_FLAG_KEY = "CP_ORG_DB_MIGRATED";

export { getEffectiveOrgSetting, DEFAULT_ORG_SETTINGS, mergeAppSettings };

export function loadCachedOrgSettings(organizationId = null) {
  try {
    const orgId = organizationId ?? getActiveCacheOrganizationId();
    const raw = readOrgScopedJson(STORAGE_KEY, orgId);
    if (!raw) {
      return { ...DEFAULT_ORG_SETTINGS };
    }
    return mergeAppSettings(DEFAULT_ORG_SETTINGS, raw);
  } catch {
    return { ...DEFAULT_ORG_SETTINGS };
  }
}

export function loadCachedOrganizationId() {
  try {
    return localStorage.getItem(ORG_ID_KEY) || getActiveCacheOrganizationId();
  } catch {
    return null;
  }
}

export function persistCachedOrgSettings(settings, organizationId = null) {
  try {
    const orgId = organizationId ?? getActiveCacheOrganizationId();
    writeOrgScopedJson(STORAGE_KEY, orgId, settings);
    if (orgId) {
      localStorage.setItem(ORG_ID_KEY, orgId);
    }
  } catch (err) {
    console.warn("[orgSettingsBridge] cache persist failed", err);
  }
}

export function hasLocalBridgeMigrationCompleted() {
  try {
    return localStorage.getItem(MIGRATION_FLAG_KEY) === "true";
  } catch {
    return false;
  }
}

export function markLocalBridgeMigrationCompleted() {
  try {
    localStorage.setItem(MIGRATION_FLAG_KEY, "true");
  } catch {
    // ignore
  }
}

/** @deprecated Use loadCachedOrgSettings */
export const loadOrgSettingsBridge = loadCachedOrgSettings;

/** @deprecated Use persistCachedOrgSettings */
export function persistOrgSettingsBridge(settings) {
  persistCachedOrgSettings(settings);
}

/** @deprecated Use mergeAppSettings + persistCachedOrgSettings */
export function mergeOrgSettingsBridge(current, patch) {
  const merged = mergeAppSettings(current, patch);
  persistCachedOrgSettings(merged);
  return merged;
}
